import { useEffect, useMemo, useState } from 'react';
import {
  fetchSessionDrivers,
  fetchCarPositions,
  fetchOutlineTrace,
  driverColour,
} from '../../services/openF1LiveClient.js';
import { fetchCurrentGpCircuit } from '../../services/circuitService.js';
import { projectXY, projectLonLat } from '../../services/circuitGeometry.js';
import Icon from '../common/Icon.jsx';

/**
 * LiveTrackMap — full-frame overlay showing car positions on the circuit.
 * -------------------------------------------------------------------------
 * Two honest modes, chosen by the session state (never faked — same invariant
 * as the rest of the app: real data or an explicit "can't show it" state):
 *
 *   LIVE   — telemetry is readable (free window, or an OpenF1 key is set). The
 *            outline is TRACED from one car's /location samples so every dot
 *            shares that exact frame and sits on the track. Positions poll on
 *            an interval and ease between samples.
 *   LOCKED — a session is running but OpenF1 withholds telemetry (401, no key).
 *            We draw the real circuit SHAPE from bacinger GeoJSON (key-free) and
 *            say plainly that positions unlock after the session / with access.
 *
 * Poll cadence matches the ease duration so motion is continuous without
 * hammering the API. Polling pauses when the tab is hidden.
 */
const POLL_MS = 2500;
const POLL_SEC = POLL_MS / 1000;

export default function LiveTrackMap({ status, session, onClose }) {
  // Escape / back to dismiss — same affordance as the article reader.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Live track map"
      className="absolute inset-0 z-40 flex flex-col bg-bg-base animate-slide-up"
    >
      <Header status={status} session={session} onClose={onClose} />
      <div className="relative flex-1 overflow-hidden">
        {status === 'live' ? (
          <LiveMap session={session} />
        ) : status === 'locked' ? (
          <LockedMap />
        ) : (
          <Centered>
            <p className="text-sm text-ink-secondary">No session is live right now.</p>
          </Centered>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function Header({ status, session, onClose }) {
  const title =
    session?.circuit_short_name || session?.location || (status === 'locked' ? 'Live session' : 'Track map');
  const subtitle = session?.session_name || (status === 'locked' ? 'In progress' : '');

  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-stroke/70 bg-bg-elevated/95 px-4 py-3 backdrop-blur-md">
      <span className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center">
        <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-f1-red animate-pulse-red" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-f1-red" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-bold text-ink-primary">{title}</h2>
        {subtitle ? <p className="truncate text-2xs text-ink-tertiary">{subtitle}</p> : null}
      </div>
      <button
        onClick={onClose}
        aria-label="Close live track map"
        className="pressable -mr-1 flex h-9 w-9 items-center justify-center rounded-full text-ink-secondary hover:text-ink-primary"
      >
        <Icon name="close" size={20} />
      </button>
    </header>
  );
}

function Centered({ children }) {
  return <div className="flex h-full flex-col items-center justify-center px-8 text-center">{children}</div>;
}

/* ---- LIVE ---------------------------------------------------------------- */

function LiveMap({ session }) {
  const [phase, setPhase] = useState('loading'); // loading | ready | no-telemetry | error
  const [outline, setOutline] = useState(null); // { d, viewBox, frame }
  const [drivers, setDrivers] = useState([]);
  const [positions, setPositions] = useState(new Map());
  const [updatedAt, setUpdatedAt] = useState(0);

  const sessionKey = session?.session_key;

  useEffect(() => {
    if (!sessionKey) return undefined;
    const ctrl = new AbortController();
    let stopped = false;
    let timer = null;

    async function poll() {
      try {
        const map = await fetchCarPositions(sessionKey, { signal: ctrl.signal });
        if (!stopped && map.size > 0) {
          setPositions(map);
          setUpdatedAt(Date.now());
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    async function init() {
      setPhase('loading');
      const roster = await fetchSessionDrivers(sessionKey, { signal: ctrl.signal });
      if (stopped) return;
      setDrivers(roster);

      // Trace the outline from one car (lowest number present tends to be a
      // front-runner that's been circulating). Same frame as the dots.
      const outlineDriver = roster.map((d) => d.driver_number).sort((a, b) => a - b)[0] ?? 1;
      const trace = await fetchOutlineTrace(sessionKey, outlineDriver, { signal: ctrl.signal });
      if (stopped) return;

      if (trace.length >= 10) {
        const proj = projectXY(trace);
        setOutline({ d: proj.d, viewBox: proj.viewBox, frame: proj.frame });
        setPhase('ready');
      } else {
        setPhase('no-telemetry');
      }
      await poll();
      timer = setInterval(() => {
        if (!document.hidden) poll();
      }, POLL_MS);
    }

    init().catch((err) => {
      if (err?.name !== 'AbortError' && !stopped) setPhase('error');
    });

    return () => {
      stopped = true;
      ctrl.abort();
      if (timer) clearInterval(timer);
    };
  }, [sessionKey]);

  // Map telemetry positions onto screen coords through the outline's frame.
  const dots = useMemo(() => {
    const frame = outline?.frame;
    if (!frame) return [];
    const out = [];
    for (const d of drivers) {
      const p = positions.get(d.driver_number);
      if (!p) continue;
      const [x, y] = frame.to(p.x, p.y);
      out.push({
        num: d.driver_number,
        tla: d.name_acronym || String(d.driver_number),
        colour: driverColour(d),
        x,
        y,
      });
    }
    return out;
  }, [outline, drivers, positions]);

  if (phase === 'loading') {
    return (
      <Centered>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stroke border-t-f1-red" />
        <p className="mt-4 text-sm text-ink-secondary">Connecting to live telemetry…</p>
      </Centered>
    );
  }
  if (phase === 'error') {
    return (
      <Centered>
        <p className="text-sm text-ink-secondary">Couldn’t reach the live telemetry feed.</p>
      </Centered>
    );
  }
  if (phase === 'no-telemetry' || !outline) {
    return (
      <Centered>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stroke border-t-f1-red" />
        <p className="mt-4 text-sm text-ink-secondary">Acquiring track telemetry…</p>
        <p className="mt-1 text-xs text-ink-tertiary">
          Waiting for enough position samples to trace the circuit.
        </p>
      </Centered>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1 overflow-hidden p-3">
        <svg viewBox={outline.viewBox} className="h-full w-full" preserveAspectRatio="xMidYMid meet">
          <path
            d={outline.d}
            fill="none"
            stroke="#2a2a32"
            strokeWidth={26}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <path
            d={outline.d}
            fill="none"
            stroke="#3a3a44"
            strokeWidth={4}
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray="2 22"
          />
          {dots.map((d) => (
            <g
              key={d.num}
              // Eases between polls; no transition fires on first mount (no
              // prior value), so a car appears in place rather than flying in.
              style={{
                transform: `translate(${d.x}px, ${d.y}px)`,
                transition: `transform ${POLL_SEC}s linear`,
              }}
            >
              <circle r={15} fill={d.colour} stroke="#050505" strokeWidth={3} />
              <text
                x={0}
                y={-24}
                textAnchor="middle"
                fontSize={20}
                fontWeight={700}
                fill="#f4f4f5"
                style={{ paintOrder: 'stroke', stroke: '#050505', strokeWidth: 4 }}
              >
                {d.tla}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <StatusLine>
        <span className="flex items-center gap-1.5 font-semibold text-f1-red">
          <span className="h-1.5 w-1.5 rounded-full bg-f1-red" />
          LIVE
        </span>
        <span>{dots.length} cars</span>
        {updatedAt ? <span>streaming</span> : null}
      </StatusLine>
    </div>
  );
}

/* ---- LOCKED -------------------------------------------------------------- */

function LockedMap() {
  const [phase, setPhase] = useState('loading'); // loading | ready
  const [circuit, setCircuit] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let stopped = false;
    fetchCurrentGpCircuit({ signal: ctrl.signal })
      .then((c) => {
        if (!stopped) {
          setCircuit(c);
          setPhase('ready');
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError' && !stopped) setPhase('ready');
      });
    return () => {
      stopped = true;
      ctrl.abort();
    };
  }, []);

  const projected = useMemo(
    () => (circuit?.coordinates ? projectLonLat(circuit.coordinates) : null),
    [circuit]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1 overflow-hidden p-3">
        {projected ? (
          <svg viewBox={projected.viewBox} className="h-full w-full opacity-40" preserveAspectRatio="xMidYMid meet">
            <path
              d={projected.d}
              fill="none"
              stroke="#3a3a44"
              strokeWidth={5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
        ) : phase === 'loading' ? (
          <Centered>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-stroke border-t-f1-red" />
          </Centered>
        ) : null}

        {/* Honest overlay — we do NOT fabricate positions. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
          <div className="max-w-xs rounded-2xl border border-stroke/70 bg-bg-elevated/90 p-5 text-center backdrop-blur-sm">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-f1-red/10 text-f1-red ring-1 ring-f1-red/30">
              <Icon name="signal" size={22} />
            </div>
            <h3 className="text-sm font-semibold text-ink-primary">
              {circuit?.name ? `${circuit.name} — live` : 'Session is live'}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-ink-secondary">
              Car positions are restricted while the session is running. They unlock automatically the
              moment it ends — or in real time with an OpenF1 key.
            </p>
          </div>
        </div>
      </div>
      <StatusLine>
        <span className="flex items-center gap-1.5 font-semibold text-signal-amber">
          <span className="h-1.5 w-1.5 rounded-full bg-signal-amber" />
          POSITIONS LOCKED
        </span>
        <span>circuit shape shown</span>
      </StatusLine>
    </div>
  );
}

function StatusLine({ children }) {
  return (
    <div className="flex shrink-0 items-center justify-center gap-3 border-t border-stroke/70 bg-bg-elevated/95 px-4 py-2.5 text-2xs uppercase tracking-wide text-ink-tertiary backdrop-blur-md">
      {children}
    </div>
  );
}
